import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Chip,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { FILTER_FIELDS } from "./dashboardConstants";
import { HEADER_COLOR, POPPINS, PRIMARY } from "./dashboardTypography";

const renderValue = (selected, options) => {
  if (!Array.isArray(selected) || selected.length === 0) return "All";
  const lookup = new Map((options || []).map((o) => [o.value, o.label]));
  return (
    <Stack direction="row" spacing={0.5} sx={{ overflowX: "auto", py: 0.25 }}>
      {selected.map((value) => (
        <Chip key={value} label={lookup.get(value) || value} size="small" />
      ))}
    </Stack>
  );
};

const GlobalFilterBar = ({
  open,
  onClose,
  filters,
  filterOptions,
  onFilterChange,
  onApply,
  onReset,
  isLoading,
}) => {
  const handleApply = () => {
    onApply();
    onClose();
  };

  const handleReset = () => {
    onReset();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          p: 2,
          backgroundColor: "#ffffff",
          "& .MuiTypography-root": {
            ...POPPINS,
          },
          "& .MuiInputBase-root": {
            ...POPPINS,
            fontSize: "0.86rem",
          },
          "& .MuiFormLabel-root": {
            ...POPPINS,
            fontSize: "0.83rem",
          },
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}>
          Dashboard Filters
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Grid container spacing={1.5}>
        {Object.entries(FILTER_FIELDS).map(([key, config]) => {
          const options = filterOptions[key] || [];
          const value = filters[key] ?? (config.multi ? [] : "");
          return (
            <Grid key={key} item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>{config.label}</InputLabel>
                <Select
                  multiple={config.multi}
                  value={value}
                  onChange={(event) => onFilterChange(key, event.target.value)}
                  input={<OutlinedInput label={config.label} />}
                  renderValue={(selected) =>
                    config.multi
                      ? renderValue(selected, options)
                      : options.find((o) => o.value === selected)?.label || "All"
                  }
                >
                  {config.multi && <MenuItem value=""><em>All</em></MenuItem>}
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          );
        })}

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Start Date"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate || ""}
            onChange={(event) => onFilterChange("startDate", event.target.value || null)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="End Date"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate || ""}
            onChange={(event) => onFilterChange("endDate", event.target.value || null)}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<RestartAltOutlinedIcon fontSize="small" />}
          onClick={handleReset}
          disabled={isLoading}
          sx={{ ...POPPINS, fontSize: "0.8rem", textTransform: "none", fontWeight: 600, borderColor: PRIMARY, color: PRIMARY }}
        >
          Reset
        </Button>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<FilterAltOutlinedIcon fontSize="small" />}
          onClick={handleApply}
          disabled={isLoading}
          sx={{ ...POPPINS, fontSize: "0.8rem", textTransform: "none", fontWeight: 600 }}
        >
          Apply Filters
        </Button>
      </Stack>
    </Drawer>
  );
};

GlobalFilterBar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  filters: PropTypes.object.isRequired,
  filterOptions: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

GlobalFilterBar.defaultProps = {
  open: false,
  onClose: () => {},
  filterOptions: {},
  isLoading: false,
};

export default GlobalFilterBar;
